import { useEffect } from 'react';

interface PageMetaOptions {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const resolveUrl = (path: string) => {
  const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${basePath}${cleanPath}`;
};

export default function usePageMeta(options: PageMetaOptions) {
  const { title, description, keywords, canonicalPath, ogType, ogImage, jsonLd } = options;

  useEffect(() => {
    document.title = title;

    upsertMeta('name', 'description', description);
    if (keywords) {
      upsertMeta('name', 'keywords', keywords);
    }
    upsertMeta('name', 'robots', 'index,follow');

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', ogType || 'website');
    upsertMeta('property', 'og:site_name', '高校吃瓜社');
    if (canonicalPath) {
      upsertMeta('property', 'og:url', resolveUrl(canonicalPath));
    }
    if (ogImage) {
      upsertMeta('property', 'og:image', ogImage);
    }

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    if (ogImage) {
      upsertMeta('name', 'twitter:image', ogImage);
    }

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', resolveUrl(canonicalPath || '/'));

    const scriptId = 'page-structured-data';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (script) {
      script.remove();
    }
    if (jsonLd) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalPath, ogType, ogImage, jsonLd]);
}